/**
 * SortableHeader — หัวคอลัมน์ที่กดเพื่อเรียงลำดับได้ (D-123 ข้อ 3)
 *
 * **ไม่รู้จักโดเมนใดเลย** ไม่รู้ว่าคอลัมน์ที่มันกำกับคือลูกค้า สินค้า หรือใบสั่งขาย
 * และ**ไม่เรียงข้อมูลเอง** — มันรายงานว่า "ผู้ใช้ขอให้เรียงคอลัมน์นี้ ทิศนี้" แล้ว
 * หน้าจอที่ถือข้อมูลเป็นผู้เรียง นั่นคือเหตุผลที่ไฟล์นี้อยู่ใน `components/` ร่วมกับ
 * `Modal`/`InfoHint` ไม่ใช่ในโฟลเดอร์ของหน้าจอใดหน้าจอหนึ่ง
 *
 * สามข้อที่เป็นเรื่องของการเข้าถึง ไม่ใช่เรื่องความสวย:
 *
 * 1. **`aria-sort` อยู่บน `<th>`** ไม่ใช่บนปุ่ม — ARIA กำหนดให้ `aria-sort` เป็น
 *    property ของ `columnheader` และ screen reader ประกาศทิศจากที่นั่น ปุ่มที่ถือ
 *    attribute นี้เองจะไม่ถูกอ่านเลย
 * 2. **ตัวที่กดได้คือ `<button>` จริง** ไม่ใช่ `<th onClick>` — หัวคอลัมน์ที่กดได้
 *    ด้วยเมาส์อย่างเดียวคือฟีเจอร์ที่ไม่มีอยู่สำหรับคนที่ใช้คีย์บอร์ด
 * 3. **ลูกศรเป็น `aria-hidden`** ทิศถูกประกาศด้วย `aria-sort` อยู่แล้ว การอ่าน
 *    "สามเหลี่ยมชี้ขึ้น" ต่อท้ายชื่อคอลัมน์ไม่ได้เพิ่มข้อมูลให้ใคร
 *
 * กติกาการกดมีสองข้อและอยู่ที่นี่ทั้งคู่ (ไม่ใช่กระจายไปที่ผู้เรียกแต่ละราย):
 * คอลัมน์ที่กำลังเรียงอยู่ → สลับทิศ, คอลัมน์อื่น → เริ่มที่ **น้อย→มาก** เสมอ
 * การเริ่มที่มาก→น้อยจะทำให้การกดครั้งแรกของแต่ละคอลัมน์ให้ผลที่เดาไม่ได้
 */

export type SortDirection = 'asc' | 'desc';

export interface SortableHeaderProps {
  /** คีย์ของคอลัมน์นี้ตามที่หน้าจอ (และ URL) เรียก */
  readonly columnKey: string;
  /** ข้อความบนหัวคอลัมน์ — เป็นชื่อที่ screen reader ประกาศให้ปุ่มด้วย */
  readonly label: string;
  /** คอลัมน์ที่กำลังเรียงอยู่จริง */
  readonly activeKey: string;
  /** ทิศของคอลัมน์ที่กำลังเรียงอยู่ */
  readonly direction: SortDirection;
  /** ผู้เรียกเป็นผู้เรียงข้อมูลและเป็นผู้จำสถานะ — component นี้ไม่ถือ state ใด ๆ */
  readonly onSort: (columnKey: string, direction: SortDirection) => void;
  readonly testId?: string;
}

/** ▲ = น้อย→มาก, ▼ = มาก→น้อย — ตรงกับทิศที่ `aria-sort` ประกาศ */
function arrowFor(direction: SortDirection): string {
  return direction === 'asc' ? '▲' : '▼';
}

export function SortableHeader({
  columnKey,
  label,
  activeKey,
  direction,
  onSort,
  testId,
}: SortableHeaderProps) {
  const active = activeKey === columnKey;
  // คอลัมน์เดิม = สลับทิศ; คอลัมน์ใหม่ = เริ่มที่น้อย→มาก (D-123 ข้อ 3)
  const nextDirection: SortDirection = active && direction === 'asc' ? 'desc' : 'asc';

  return (
    <th
      scope="col"
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      data-testid={testId}
      data-sort-key={columnKey}
    >
      <button
        type="button"
        className={`cap-sort${active ? ' cap-sort--active' : ''}`}
        onClick={() => {
          onSort(columnKey, nextDirection);
        }}
      >
        {label}
        {active ? (
          <span className="cap-sort__arrow" aria-hidden="true">
            {arrowFor(direction)}
          </span>
        ) : null}
      </button>
    </th>
  );
}
